import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'find-and-replace-regex-cheat-sheet',
  title: 'Find & Replace with Regular Expressions: A Practical Cheat Sheet',
  description:
    'Ready-to-use regex patterns for find and replace: dates, emails, phone numbers, extra spaces, swapping names and more, with capture-group examples.',
  category: 'Text editor',
  publishedDate: '2026-11-03',
  affiliateCategory: 'writing',
  body: `
Open find & replace in the [Text Editor](/text-editor) with **Ctrl+F** (⌘F on Mac). Besides plain text search, three toggles change how matching works:

- **Aa** — match case exactly.
- **ab** — whole words only (\`cat\` won't match inside \`concat\`).
- **.\\*** — treat the search as a **regular expression**.

Press **Enter** for the next match, **Shift+Enter** for the previous one, and use **Replace** or **All**. The counter shows which match you're on ("3 of 12"). Replace-all is a single undo step.

## Regex building blocks

| Pattern | Matches |
|---|---|
| \`.\` | any character except a line break |
| \`\\d\` / \`\\w\` / \`\\s\` | a digit / a letter, digit or underscore / whitespace |
| \`[abc]\` / \`[^abc]\` | one of a, b, c / anything except them |
| \`+\` / \`*\` / \`?\` | one or more / zero or more / optional |
| \`{2,4}\` | between 2 and 4 times |
| \`^\` / \`$\` | start / end of a **line** |
| \`\\b\` | a word boundary |
| \`(…)\` | a capture group, reused as \`$1\`, \`$2\`… in the replacement |
| \`a\\|b\` | a or b |

Escape special characters with a backslash when you mean them literally: \`\\.\`, \`\\(\`, \`\\$\`.

## In the replacement

- \`$1\`, \`$2\` … — the text captured by each group.
- \`$&\` — the whole match.
- \`$<name>\` — a named group written as \`(?<name>…)\`.
- \`\\n\` and \`\\t\` — a line break and a tab.
- \`$$\` — a literal dollar sign.

## Copy-paste recipes

**Collapse repeated spaces**
Find \`[ \\t]{2,}\` → Replace with a single space.

**Delete blank lines**
Find \`^\\s*$\\n\` → Replace with nothing.

**Remove trailing spaces at line ends**
Find \`[ \\t]+$\` → Replace with nothing.

**Swap "Last, First" to "First Last"**
Find \`(\\w+), (\\w+)\` → Replace \`$2 $1\`.

**Reformat dates from 2026-11-03 to 03/11/2026**
Find \`(\\d{4})-(\\d{2})-(\\d{2})\` → Replace \`$3/$2/$1\`.

**Wrap every line in quotes and add a comma** (great for SQL \`IN (…)\` lists)
Find \`^(.+)$\` → Replace \`'$1',\`.

**Put each sentence on its own line**
Find \`([.!?]) +\` → Replace \`$1\\n\`.

**Find email addresses**
\`[\\w.+-]+@[\\w-]+\\.[\\w.]+\`

**Find phone numbers like 555-123-4567 or (555) 123 4567**
\`\\(?\\d{3}\\)?[ .-]?\\d{3}[ .-]?\\d{4}\`

**Find words that appear twice in a row** ("the the")
\`\\b(\\w+) \\1\\b\` → Replace \`$1\`.

**Mask all but the last 4 digits of a long number**
Find \`\\d(?=\\d{4})\` → Replace \`*\`.

## Tips

- **Test before replacing all:** type the pattern and watch the match counter; step through a few matches with Enter first.
- **Invalid pattern?** The counter says "Invalid pattern" and nothing is replaced.
- **Many simple cleanups don't need regex.** The editor's text tools already include *Collapse multiple spaces*, *Remove empty lines*, *Remove duplicate lines*, *Remove URLs & emails* and more — see [cleaning up PDF text](/guides/clean-up-text-copied-from-pdf).
`,
};

export default guide;
